import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, TextField } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

const OperatorCard = ({ operator, isPreview = false, isExpressionItem = false, expressionIndex, onPropertyChange }) => {
    const [hover, setHover] = useState(false);
    const [factorPreviews, setFactorPreviews] = useState({});

    const [{ isDragging }, drag, preview] = useDrag(() => {
        const item = isExpressionItem
            ? { operator, fromExpression: true, originalIndex: expressionIndex }
            : { operator };
        return {
            type: 'operator',
            item,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        };
    }, [operator, isExpressionItem, expressionIndex]);

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    const cardStyles = {
        minWidth: 150,
        width: 250,
        m: 1,
        opacity: isDragging && !isPreview ? 0.5 : 1,
        transform: isPreview ? 'scale(1.05)' : 'none',
        transition: 'transform 0.2s ease',
        cursor: isExpressionItem ? 'grab' : 'default'
    };

    const commitFactorChange = (idx, value) => {
        if (onPropertyChange) {
            const newFactors = operator.factors ? [...operator.factors] : [];
            newFactors[idx] = { ...newFactors[idx], value };
            onPropertyChange(expressionIndex, { factors: newFactors });
        }
    };

    const handleChange = (e, factorIndex) => {
        if (!onPropertyChange) return;
        if (typeof factorIndex === 'number') {
            // Factor changes via slider are handled on release.
        } else {
            onPropertyChange(expressionIndex, { label: e.target.value });
        }
    };

    return (
        <Card sx={{ ...cardStyles, position: 'relative' }}>
            <CardContent>
                <Box
                    ref={drag}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        cursor: 'grab',
                        transition: 'transform 0.15s ease',
                        transform: hover ? 'scale(1.1)' : 'scale(1)'
                    }}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    <DragIndicatorIcon fontSize="medium" />
                    <Typography variant="h6">
                        {operator.name} {isExpressionItem && operator.label && `(${operator.label})`} 
                    </Typography>
                </Box>

                {/* Insert version number badge at top right of card */}
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontWeight: 'bold'
                    }}
                >
                    {operator.version && `v${operator.version}`}
                </Typography>

                {isExpressionItem ? (
                    <Box sx={{ mt: 1 }}>
                        {operator.factors && operator.factors.length > 0 && operator.factors.map((factor, idx) => {
                            // Use operator.response_model for range and default_value.
                            const min = operator.response_model.range[0];
                            const max = operator.response_model.range[1];
                            const effectiveValue = factorPreviews[idx] !== undefined 
                                ? factorPreviews[idx] 
                                : (factor.value !== undefined ? factor.value : operator.response_model.default_value);
                            const effectiveNum = Number(effectiveValue);
                            const ratio = (effectiveNum - min) / (max - min);
                            return (
                                <Box key={idx} sx={{ mb: 2, position: 'relative' }}>
                                    {/* Display current factor value next to its label */}
                                    <Typography variant="caption">
                                        {factor.label} ({effectiveValue})
                                    </Typography>
                                    {factorPreviews[idx] !== undefined && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                left: `${5 + ratio * 90}%`,
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'grey.700',
                                                color: 'white',
                                                borderRadius: '9999px',
                                                px: 1,
                                                py: 0.5,
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {factorPreviews[idx]}
                                        </Box>
                                    )}
                                    <Box sx={{ position: 'relative' }}>
                                        <input
                                            type="range"
                                            min={min}
                                            max={max}
                                            value={effectiveValue}
                                            style={{ width: '100%' }}
                                            onChange={(e) => {
                                                setFactorPreviews(prev => ({ ...prev, [idx]: e.target.value }));
                                            }}
                                            onMouseUp={(e) => {
                                                commitFactorChange(idx, e.target.value);
                                                setFactorPreviews(prev => {
                                                    const updated = { ...prev };
                                                    delete updated[idx];
                                                    return updated;
                                                });
                                            }}
                                            onTouchEnd={(e) => {
                                                commitFactorChange(idx, e.target.value);
                                                setFactorPreviews(prev => {
                                                    const updated = { ...prev };
                                                    delete updated[idx];
                                                    return updated;
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                        <TextField
                            label="Add a label"
                            variant="outlined"
                            size="small"
                            value={operator.label || ''}
                            onChange={handleChange}
                        />
                    </Box>
                ) : (
                    <>
                        {/* Created date at top of description */}
                        <Typography variant="caption" sx={{ color: 'grey.600', mb: 1 }}>
                            {operator.created_at && new Date(operator.created_at).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                                {operator.description || 'No expression information available.'}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body3">Factors:</Typography>
                                {operator.factors && operator.factors.length > 0 && operator.factors.map((factor, idx) => (
                                    <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="caption">
                                            <b>"{factor.label}"</b>: {factor.items_included.length} items, [{operator.response_model.range[0]}, {operator.response_model.range[1]}], Default {operator.response_model.default_value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontStyle: 'italic',
                                position: 'absolute',
                                bottom: 8,
                                right: 8
                            }}
                        >
                            {operator.authors && operator.authors.join(', ')}
                        </Typography>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default OperatorCard;